import React, { useEffect, useState } from 'react';
import axios from '../../../config/axios';
import { useTranslation } from 'react-i18next';
import { ChevronLeft, ChevronRight, Search } from 'lucide-react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faUser, faPlus, faFilter } from '@fortawesome/free-solid-svg-icons';
import { LoaderCircle, Pen } from 'lucide-react';

import AddLearnerFormateurModal from '../../../components/Modals/learner/add-modal-formateur';
import EditApprenantForm from '../../../components/Modals/learner/EditApprenantForm';
import { AnimatePresence } from 'framer-motion';

/**
 * Composant principal qui gère la liste des apprenants, les filtres, 
 * les modales et la pagination.
 */
function LearnerListFormateur() {
  const [apprenants, setApprenants] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [showFilter, setShowFilter] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [isModalOpenAjout, setIsModalOpenAjout] = useState(false);
  const [isModalOpenEdit, setIsModalOpenEdit] = useState(false);
  const [selectedApprenant, setSelectedApprenant] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  const { t } = useTranslation();

  const endpoint = 'https://formafusionmg.ams3.cdn.digitaloceanspaces.com/formafusionmg';
  const itemsPerPage = 12;

  const fetchApprenants = async () => {
    try {
      const { data } = await axios.get('/form/apprenants/index');
      setApprenants(data.apprenants);
      setLoading(false);
      setError("");
    } catch (err) {
      setLoading(false);
      if (err.response) {
        if (err.response.status === 404) {
          setApprenants([]);
        } else {
          setError("Vous n'avez pas l'autorisation nécessaire pour visiter cette adresse !");
        }
      } else {
        setError("Erreur réseau ou serveur injoignable.");
      }
    }
  };

  useEffect(() => {
    fetchApprenants();
  }, []);

  const handleOpenModalAjout = () => {
    setIsModalOpenAjout(true);
  };

  const closeModalAjout = () => {
    setIsModalOpenAjout(false);
    fetchApprenants();
  };

  const handleOpenModalEdit = (apprenant) => {
    setSelectedApprenant(apprenant);
    setIsModalOpenEdit(true);
  };

  const closeModalEdit = () => {
    setIsModalOpenEdit(false);
    setSelectedApprenant(null);
    fetchApprenants();
  };

  const handleFilterChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1); // Réinitialiser la page à 1 lors de la recherche
  };

  const filteredApprenants = searchTerm
    ? apprenants.filter(apprenant => {
      const searchLower = searchTerm.toLowerCase();
      return (
        (apprenant.emp_name && apprenant.emp_name.toLowerCase().includes(searchLower)) ||
        (apprenant.emp_firstname && apprenant.emp_firstname.toLowerCase().includes(searchLower)) ||
        (apprenant.etp_name && apprenant.etp_name.toLowerCase().includes(searchLower))
      );
    })
    : apprenants;

  // Logique de pagination
  const indexOfLastApprenant = currentPage * itemsPerPage;
  const indexOfFirstApprenant = indexOfLastApprenant - itemsPerPage;
  const currentApprenants = filteredApprenants.slice(indexOfFirstApprenant, indexOfLastApprenant);
  const totalPages = Math.ceil(filteredApprenants.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  if (loading) {
    return (
      <div className="inset-0 bg-white/20 backdrop-blur-none flex items-center justify-center z-10 rounded-2xl p-10">
        <div className="flex flex-col items-center gap-4">
          <div className="relative">
            <LoaderCircle className="w-6 h-6 mx-auto animate-spin text-purple-500 border-purple-500" />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/70">
        <div className="bg-white rounded-2xl shadow-2xl p-8 w-[90%] max-w-md mx-auto">
          <div className="text-red-500 text-center text-5xl animate-bounce">⚠️</div>
          <h3 className="text-xl text-center font-semibold text-gray-800 mb-4">Une erreur est survenue</h3>
          <p className="text-gray-600 text-center mb-6">{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-5">
      <div className="flex justify-between items-center mb-2 gap-4">
        <p>Tous les apprenants</p>
        <div className="flex justify-end items-center mb-2 gap-4">
          <div className="relative  flex items-center  rounded  transition-all duration-200 hover:shadow-xl focus-within:ring-2 focus-within:ring-blue-500/20">
            <Search className="absolute left-2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder={t('Rechercher ici')}
              value={searchTerm}
              onChange={handleFilterChange}
              className="px-8 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <button onClick={handleOpenModalAjout} className="flex items-center gap-2 px-4 py-2 text-white bg-purple-600 rounded-lg hover:bg-green-700 transition-colors">
            <FontAwesomeIcon icon={faPlus} />
            <span>Nouveau</span>
          </button>
        </div>
      </div>
      {/* Rendu des apprenants de la page courante */}
      <ApprenantList
        filteredApprenants={currentApprenants}
        noResult={filteredApprenants.length === 0}
        searchTerm={searchTerm}
        t={t}
        endpoint={endpoint}
        openEditModal={handleOpenModalEdit}
      />

      {/* Rendu de la pagination si le nombre de pages est supérieur à 1 */}
      {totalPages > 1 && (
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      )}
      <AnimatePresence>
        {isModalOpenAjout && (
          <AddLearnerFormateurModal
            type="addApprenant"
            closeModal={closeModalAjout}
            isOpen={isModalOpenAjout}
            fetchApprenants={fetchApprenants}
          />
        )}

        {isModalOpenEdit && selectedApprenant && (
          <EditApprenantForm
            type="editApprenantForm"
            closeModal={closeModalEdit}
            isOpen={isModalOpenEdit}
            idEmploye={selectedApprenant.idEmploye}
            apprenant={selectedApprenant}
            fetchApprenants={fetchApprenants}
          />
        )}
      </AnimatePresence>

    </div>
  );
}

/**
 * Composant de pagination qui affiche les numéros de page et les boutons de navigation.
 */
const Pagination = ({
  currentPage,
  totalPages,
  onPageChange
}) => {
  const renderPageNumbers = () => {
    const pages = [];
    const delta = 2;

    const start = Math.max(1, currentPage - delta);
    const end = Math.min(totalPages, currentPage + delta);

    if (start > 1) {
      pages.push(1);
      if (start > 2) {
        pages.push('...');
      }
    }

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (end < totalPages) {
      if (end < totalPages - 1) {
        pages.push('...');
      }
      pages.push(totalPages);
    }

    return pages;
  };

  const pageNumbers = renderPageNumbers();

  return (
    <div className="flex items-center justify-center space-x-1 py-4">
      {/* Bouton Previous */}
      <button
        onClick={() => onPageChange(currentPage - 1)}
        disabled={currentPage === 1}
        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${currentPage === 1 ? 'text-gray-400 cursor-not-allowed bg-gray-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
      >
        <div className="flex items-center space-x-1">
          <ChevronLeft className="w-4 h-4" />
          <span>Previous</span>
        </div>
      </button>

      {/* Numéros de page */}
      <div className="flex items-center space-x-1">
        {pageNumbers.map((page, index) => (
          <React.Fragment key={index}>
            {page === '...' ? (
              <span className="px-3 py-2 text-gray-500">...</span>
            ) : (
              <button
                onClick={() => onPageChange(page)}
                className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 min-w-[40px] ${page === currentPage ? 'bg-blue-600 text-white shadow-md hover:bg-blue-700' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
              >
                {page}
              </button>
            )}
          </React.Fragment>
        ))}
      </div>

      {/* Bouton Next */}
      <button
        onClick={() => onPageChange(currentPage + 1)}
        disabled={currentPage === totalPages}
        className={`px-3 py-2 text-sm font-medium rounded-lg transition-all duration-200 ${currentPage === totalPages ? 'text-gray-400 cursor-not-allowed bg-gray-50' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-100'}`}
      >
        <div className="flex items-center space-x-1">
          <span>Next</span>
          <ChevronRight className="w-4 h-4" />
        </div>
      </button>
    </div>
  );
};

/**
 * Composant qui rend la liste des cartes d'apprenants.
 */
const ApprenantList = ({ filteredApprenants, noResult, searchTerm, t, endpoint, openEditModal }) => {
  if (noResult || (searchTerm && filteredApprenants.length === 0)) {
    return (
      <div className="text-center mt-10 text-gray-500 text-lg">
        {t('no_results')}
      </div>
    );
  }
  return (
    <div className="grid grid-cols-1 gap-2 lg:grid-cols-3 md:grid-cols-2">
      {filteredApprenants.map((apprenant) => (
        <ApprenantCard
          key={apprenant.idEmploye}
          apprenant={apprenant}
          t={t}
          endpoint={endpoint}
          openEditModal={openEditModal}
        />
      ))}
    </div>
  );
};

/**
 * Composant pour une carte d'apprenant individuelle.
 */
const ApprenantCard = ({ apprenant, t, endpoint, openEditModal }) => {
  const handleEditClick = () => {
    openEditModal(apprenant);
  };

  return (
    <div className="relative group hover:scale-[1.02] transition-all duration-300 col-span-1 p-6 bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-lg hover:shadow-xl rounded-2xl h-[320px]  overflow-hidden">
      <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-indigo-500"></div>

      <div className="absolute top-6 right-6   transition-all duration-300 transform translate-y-[-4px] group-hover:translate-y-0">
        {apprenant.isAddedByTrainer && (
          <button
            onClick={handleEditClick}
            className="w-9 h-9 flex items-center justify-center text-white 
                       
                        
                        rounded-xl border border-blue-500 
                        transition-all duration-200 hover:scale-110"
            title={t('edit')}
          >
            <Pen className="h-5 w-5 text-blue-500" />
          </button>
        )}

      </div>

      <div className="flex flex-col h-full">
        <div className="flex items-start mb-6">
          <div className="relative">
            <div className="flex items-center justify-center border-2 border-gray-200 w-20 h-20 text-2xl font-medium text-gray-500 uppercase bg-gradient-to-br from-gray-100 to-gray-200 rounded-full shadow-inner overflow-hidden">
              {apprenant.emp_photo ? (
                <img
                  src={`${endpoint}/img/employes/${apprenant.emp_photo}`}
                  alt="photo"
                  className="object-cover w-full h-full"
                />
              ) : (
                <FontAwesomeIcon icon={faUser} className="text-3xl text-gray-500" />
              )}
            </div>
            <div className="absolute -bottom-1 -right-1 w-4 h-4 bg-green-400 border-2 border-white rounded-full shadow-sm"></div>
          </div>

          <div className="ml-4 flex-1 min-w-0">
            <h3 className="text-lg font-bold text-gray-900 leading-tight mb-1 truncate">
              {apprenant.emp_name}
            </h3>
            <p className="text-lg font-semibold text-gray-700 leading-tight truncate">
              {apprenant.emp_firstname}
            </p>
          </div>
        </div>

        <div className="flex-1 space-y-2 ">
          <div className=" p-3 rounded-xl border border-purple-100">

            <p className="text-sm font-semibold text-purple-600 line-clamp-1 leading-tight">
              {apprenant.etp_name}
            </p>
          </div>
          <div className=" p-3 rounded-xl border border-blue-100">

            <p className="text-sm font-semibold text-blue-600 line-clamp-2 leading-tight">
              {apprenant.project_title}
            </p>
          </div>



          <div className=" p-3 rounded-xl border border-green-100">
            <p className="text-xs font-medium text-green-600 uppercase tracking-wide mb-1">Module</p>
            <p className="text-sm font-semibold text-gray-800 line-clamp-1 leading-tight">
              {apprenant.module_name}
            </p>
          </div>
        </div>
      </div>
      <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-10 transform -skew-x-12 translate-x-[-100%] group-hover:translate-x-[100%] transition-all duration-700"></div>
    </div>
  );
};

export default LearnerListFormateur;